/**
 * PDP reviews drawer — inject Klaviyo placeholder on first open only.
 *
 * Drawer uses the standard functional dev-drawer shell (like help center nav).
 * Mount runs on trigger click (capture) before showDialog, with dialog:open as fallback.
 * After Klaviyo hydrates, filter icons are swapped once for DS icons.
 *
 * Review photo click closes the drawer so Klaviyo’s body-mounted lightbox is not trapped
 * under the top-layer `<dialog>` (Prestige-style z-index then works).
 */

const DRAWER_ID = 'dev-product-reviews-drawer';
const HOST_ATTR = 'data-dev-reviews-klaviyo-host';
const KLAVIYO_MOUNTED_ATTR = 'data-dev-reviews-klaviyo-mounted';
const LOADING_READY_TIMEOUT_MS = 20000;
const FILTER_CHEVRON_TEMPLATE_ID = 'dev-product-reviews-filter-chevron';
const DS_CHEVRON_ATTR = 'data-dev-ds-chevron';

/** @param {Element} drawer */
function getReviewsLoading(drawer) {
  return drawer.querySelector('.dev-product-reviews-drawer__loading');
}

/** @param {Element} drawer @param {boolean} isLoading */
function setReviewsLoading(drawer, isLoading) {
  const loading = getReviewsLoading(drawer);
  if (!(loading instanceof HTMLElement)) return;

  loading.hidden = !isLoading;
  drawer.classList.toggle('dev-product-reviews-drawer--loading', isLoading);
}

/** @param {Element} drawer */
function isKlaviyoReady(drawer) {
  const host = drawer.querySelector(`[${HOST_ATTR}]`);
  if (!(host instanceof HTMLElement)) return false;

  return Boolean(host.querySelector('#klaviyo-product-reviews-wrapper'));
}

/** @param {Element} drawer */
function markReviewsReady(drawer) {
  if (!isKlaviyoReady(drawer)) return false;

  setReviewsLoading(drawer, false);
  drawer.classList.add('dev-product-reviews-drawer--ready');
  return true;
}

/** @param {Element} drawer */
function watchKlaviyoReady(drawer) {
  const host = drawer.querySelector(`[${HOST_ATTR}]`);
  if (!(host instanceof HTMLElement)) return;

  if (markReviewsReady(drawer)) return;

  let frameScheduled = false;

  const checkReady = () => {
    frameScheduled = false;
    if (markReviewsReady(drawer)) {
      readyObserver.disconnect();
    }
  };

  const scheduleReadyCheck = () => {
    if (frameScheduled) return;
    frameScheduled = true;
    requestAnimationFrame(checkReady);
  };

  const readyObserver = new MutationObserver(scheduleReadyCheck);

  readyObserver.observe(host, { childList: true, subtree: true });

  window.setTimeout(() => {
    readyObserver.disconnect();
    setReviewsLoading(drawer, false);
  }, LOADING_READY_TIMEOUT_MS);

  scheduleReadyCheck();
}

/** @param {Element} drawer */
function fitFilterSelectWidths(drawer) {
  const selects = drawer.querySelectorAll('.kl_reviews__filters__pill select');

  for (const select of selects) {
    if (!(select instanceof HTMLSelectElement)) continue;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) continue;

    const measurer = document.createElement('span');
    measurer.textContent = selectedOption.text;
    measurer.setAttribute('aria-hidden', 'true');
    measurer.style.cssText =
      'position:absolute;visibility:hidden;pointer-events:none;white-space:nowrap;height:0;overflow:hidden;';

    const styles = getComputedStyle(select);
    measurer.style.fontFamily = styles.fontFamily;
    measurer.style.fontSize = styles.fontSize;
    measurer.style.fontWeight = styles.fontWeight;
    measurer.style.letterSpacing = styles.letterSpacing;

    select.parentElement?.appendChild(measurer);
    select.style.width = `${measurer.offsetWidth}px`;
    measurer.remove();
  }
}

/** @param {Element} drawer */
function bindFilterSelectResize(drawer) {
  if (drawer.dataset.devReviewsFilterResizeBound === 'true') return;

  drawer.dataset.devReviewsFilterResizeBound = 'true';

  drawer.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.closest('.kl_reviews__filters__pill')) return;
    fitFilterSelectWidths(drawer);
  });
}

/** @param {Element} drawer */
function replaceFilterChevrons(drawer) {
  const template = document.getElementById(FILTER_CHEVRON_TEMPLATE_ID);
  if (!(template instanceof HTMLTemplateElement)) return;

  const pills = drawer.querySelectorAll('.kl_reviews__filters__pill:has(select)');

  for (const pill of pills) {
    const nativeSvg = pill.querySelector('svg');
    if (!(nativeSvg instanceof SVGElement) || nativeSvg.hasAttribute(DS_CHEVRON_ATTR)) {
      continue;
    }

    const clone = template.content.cloneNode(true);
    const dsSvg = clone.querySelector('svg');
    if (!(dsSvg instanceof SVGElement)) continue;

    dsSvg.setAttribute(DS_CHEVRON_ATTR, '');
    nativeSvg.replaceWith(dsSvg);
  }
}

/** @param {Element} drawer */
function removeMediaFilterNativeIcons(drawer) {
  const buttons = drawer.querySelectorAll(
    '.kl_reviews__filter_with_left_align_icon button, .kl_reviews__filters__pill button[role="checkbox"]'
  );

  for (const button of buttons) {
    if (!(button instanceof HTMLButtonElement)) continue;

    for (const svg of button.querySelectorAll('svg')) {
      svg.remove();
    }
  }
}

/** @param {Element} drawer */
function iconsNeedPatch(drawer) {
  const needsChevron = drawer.querySelector(
    '.kl_reviews__filters__pill:has(select) svg:not([' + DS_CHEVRON_ATTR + '])'
  );
  const needsMediaIcon = drawer.querySelector(
    '.kl_reviews__filter_with_left_align_icon button svg, .kl_reviews__filters__pill button[role="checkbox"] svg'
  );

  return Boolean(needsChevron || needsMediaIcon);
}

/**
 * @param {Element} drawer
 * @param {{ fitWidths?: boolean }} [options]
 */
function patchKlaviyoUi(drawer, { fitWidths = false } = {}) {
  const host = drawer.querySelector(`[${HOST_ATTR}]`);
  if (!(host instanceof HTMLElement)) return false;

  const wrapper = host.querySelector('#klaviyo-product-reviews-wrapper');
  if (!wrapper) return false;

  replaceFilterChevrons(drawer);
  removeMediaFilterNativeIcons(drawer);

  if (fitWidths) {
    fitFilterSelectWidths(drawer);
  }

  return true;
}

/** @param {Element} drawer */
function watchKlaviyoUiPatch(drawer) {
  const host = drawer.querySelector(`[${HOST_ATTR}]`);
  if (!(host instanceof HTMLElement)) return;

  let filtersFitted = false;
  let frameScheduled = false;

  const runPatch = () => {
    frameScheduled = false;

    const shouldFitWidths = !filtersFitted && Boolean(drawer.querySelector('.kl_reviews__filters__pill select'));
    if (!patchKlaviyoUi(drawer, { fitWidths: shouldFitWidths })) return;

    if (shouldFitWidths) {
      filtersFitted = true;
    }

    if (!iconsNeedPatch(drawer)) {
      observer.disconnect();
    }
  };

  const schedulePatch = () => {
    if (frameScheduled) return;
    frameScheduled = true;
    requestAnimationFrame(runPatch);
  };

  const observer = new MutationObserver(schedulePatch);

  observer.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-checked'] });

  window.setTimeout(() => {
    observer.disconnect();
  }, 15000);

  schedulePatch();
}

/** @param {Element} drawer */
function mountKlaviyoOnce(drawer) {
  const host = drawer.querySelector(`[${HOST_ATTR}]`);
  if (!(host instanceof HTMLElement) || host.getAttribute(KLAVIYO_MOUNTED_ATTR) === 'true') {
    return;
  }

  host.setAttribute(KLAVIYO_MOUNTED_ATTR, 'true');
  setReviewsLoading(drawer, true);

  const placeholder = document.createElement('div');
  placeholder.id = 'klaviyo-reviews-all';
  placeholder.dataset.id = host.dataset.productId || '';

  if (host.dataset.productTitle) {
    placeholder.dataset.productTitle = host.dataset.productTitle;
  }

  if (host.dataset.productType) {
    placeholder.dataset.productType = host.dataset.productType;
  }

  host.appendChild(placeholder);
  bindFilterSelectResize(drawer);
  bindFilterIconRefresh(drawer);
  watchKlaviyoReady(drawer);
  watchKlaviyoUiPatch(drawer);
}

/** @param {Element} drawer */
function bindFilterIconRefresh(drawer) {
  drawer.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const mediaButton = target.closest(
        '.kl_reviews__filter_with_left_align_icon button, .kl_reviews__filters__pill button[role="checkbox"]'
      );
      if (!mediaButton) return;

      requestAnimationFrame(() => {
        removeMediaFilterNativeIcons(drawer);
        requestAnimationFrame(() => {
          removeMediaFilterNativeIcons(drawer);
        });
      });
    },
    true
  );
}

/** @param {Element} drawer */
function bindKlaviyoLazyMount(drawer) {
  const mount = () => {
    mountKlaviyoOnce(drawer);
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest(`[aria-controls="${DRAWER_ID}"]`);
      if (!trigger) return;

      mount();
    },
    true
  );

  drawer.addEventListener('dialog:open', mount);
}

/**
 * Close the reviews drawer when a review photo is opened so the Klaviyo lightbox
 * is not covered by the top-layer dialog.
 * @param {Element} drawer
 */
function bindCloseOnReviewPhoto(drawer) {
  drawer.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('.kl_reviews__review__image_container')) return;

      if ('closeDialog' in drawer && typeof drawer.closeDialog === 'function') {
        drawer.closeDialog();
      }
    },
    true
  );
}

const drawer = document.getElementById(DRAWER_ID);
if (drawer instanceof HTMLElement) {
  bindKlaviyoLazyMount(drawer);
  bindCloseOnReviewPhoto(drawer);
}
